import React from 'react';
import { TableColumn } from '@hero/ui-kit/tables/Table';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import { useLocation, useNavigate } from 'react-router-dom';
import BlastStatusPill from './BlastStatusPill';
import type { BlastSummary } from '../../types';
const NameRender: React.FC<{
    blast: BlastSummary;
}> = ({ blast }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const handleClick = () => {
        const from = `${location.pathname}${location.search}`;
        navigate(`/mark/blasts/${blast.blast_id}`, { state: { from } });
    };
    return (
        <ClickableOpacity
            onClick={handleClick}
            alt="view-blast"
            styles={{
                cursor: 'pointer'
            }}
        >
            <P noDefaultMargin strong>
                <span
                    style={{
                        borderBottom: '1px solid var(--mark-primary)'
                    }}
                >
                    {blast.name}
                </span>
            </P>
        </ClickableOpacity>
    );
};
const formatRate = (rate: number | null): string => {
    if (rate == null) return '—';
    return `${(rate * 100).toFixed(1)}%`;
};
const getBlastColumns = (): TableColumn<BlastSummary>[] => [
    {
        colKey: 'name',
        name: 'Name',
        align: 'left',
        headAlign: 'left',
        render: (blast) => <NameRender blast={blast} />
    },
    {
        colKey: 'status',
        name: 'Status',
        align: 'left',
        headAlign: 'left',
        render: (blast) => <BlastStatusPill status={blast.status} />
    },
    {
        colKey: 'message_class',
        name: 'Type',
        align: 'left',
        headAlign: 'left',
        render: (blast) => (
            <Tag type={blast.message_class === 'TRANSACTIONAL' ? 'warn' : 'primary'} size="small">
                {blast.message_class}
            </Tag>
        )
    },
    {
        colKey: 'segment_name',
        name: 'Segment',
        align: 'left',
        headAlign: 'left',
        render: (blast) => <P noDefaultMargin>{blast.segment_name ?? '—'}</P>
    },
    {
        colKey: 'scheduled_at',
        name: 'Scheduled',
        align: 'left',
        headAlign: 'left',
        render: (blast) => (
            <P noDefaultMargin>
                {blast.scheduled_at
                    ? new Date(blast.scheduled_at).toLocaleString(undefined, {
                          timeZoneName: 'short'
                      })
                    : '—'}
            </P>
        )
    },
    {
        colKey: 'sent_count',
        name: 'Sent',
        align: 'right',
        headAlign: 'right',
        render: (blast) => (
            <P noDefaultMargin styles={{ textAlign: 'right', width: '100%' }}>
                {blast.sent_count}
                {blast.recipient_count != null ? ` / ${blast.recipient_count}` : ''}
            </P>
        )
    },
    {
        colKey: 'open_rate',
        name: 'Open rate',
        align: 'right',
        headAlign: 'right',
        render: (blast) => (
            <P noDefaultMargin styles={{ textAlign: 'right', width: '100%' }}>
                {formatRate(blast.open_rate)}
            </P>
        )
    },
    {
        colKey: 'unsubscribe_rate',
        name: 'Unsub rate',
        align: 'right',
        headAlign: 'right',
        render: (blast) => (
            <P noDefaultMargin styles={{ textAlign: 'right', width: '100%' }}>
                {formatRate(blast.unsubscribe_rate)}
            </P>
        )
    }
];
export default getBlastColumns;
